import { useElementSize, useMutationObserver, useDebounceFn } from '@vueuse/core';
import { ref, watch, onMounted, type Ref, type ComponentPublicInstance } from 'vue';

const useNavScroll = (
  activeScrollItemIndex: Ref<number | null>,
  navRef: Ref<HTMLElement | null>,
  navItemRefs: Ref<(Element | ComponentPublicInstance | null)[]>,
  variant: 'links' | 'buttons'
) => {
  const scrollLeftVisible = ref(false);
  const scrollRightVisible = ref(false);

  const SCROLLBY_OFFSET_PX = 100;

  const { width } = useElementSize(navRef);

  onMounted(() => {
    useMutationObserver(
      document.head,
      () => {
        setNavScrollPosition();
      },
      {
        childList: true,
        subtree: true,
      }
    );
  });

  const onScrollIconClick = (sign: number) => {
    if (!navRef.value) return;

    const maxScroll = navRef.value.scrollWidth - navRef.value.clientWidth;
    const target = Math.max(
      0,
      Math.min(maxScroll, navRef.value.scrollLeft + sign * SCROLLBY_OFFSET_PX)
    );

    navRef.value.scrollTo({ left: target, behavior: 'smooth' });
    checkScrollVisibility();
  };

  const onScroll = () => {
    checkScrollVisibility();
  };

  const checkScrollVisibility = () => {
    const SCROLL_TOLERANCE_PX = 9;

    if (navRef.value) {
      const scrollVisible = navRef.value.clientWidth < navRef.value.scrollWidth;
      const maxScroll = navRef.value.scrollWidth - navRef.value.clientWidth;

      const scrollOnLeft = navRef.value.scrollLeft <= 0;
      const scrollOnRight = navRef.value.scrollLeft >= maxScroll - SCROLL_TOLERANCE_PX;

      scrollLeftVisible.value = scrollVisible && !scrollOnLeft;
      scrollRightVisible.value = scrollVisible && !scrollOnRight;
    }
  };

  const setNavScrollPosition = useDebounceFn(() => {
    if (!navRef.value || !navItemRefs.value || activeScrollItemIndex.value == null) return;

    // This condition is required because router-link is not resolved as HTML element by ref
    const itemRef = navItemRefs.value[activeScrollItemIndex.value];
    const elRef =
      variant === 'links' && itemRef && '$el' in itemRef
        ? (itemRef as ComponentPublicInstance).$el
        : itemRef;

    if (!elRef) return;

    const navPaddingLeft = Number.parseFloat(
      globalThis.getComputedStyle(navRef.value)?.getPropertyValue('padding-left')
    );

    const elMarginLeft = Number.parseFloat(
      globalThis.getComputedStyle(elRef)?.getPropertyValue('margin-left')
    );

    navRef.value.scrollLeft =
      elRef.offsetLeft - elMarginLeft - navRef.value.offsetLeft - navPaddingLeft;
  }, 1);

  watch([activeScrollItemIndex, width], async () => {
    if (!width.value) {
      return;
    }

    setNavScrollPosition();
    checkScrollVisibility();
  });

  return {
    onScroll,
    onScrollIconClick,
    scrollLeftVisible,
    scrollRightVisible,
  };
};

export default useNavScroll;
